import { action, computed, makeObservable, observable, reaction, runInAction } from "mobx"

import type { IEventBus } from "@/domains/events"

import type {
  ISwapAggregatorPluginRegistry,
  ISwapAggregatorSettingsStore,
  ISwapInputStore,
  ISwapQuoteStore,
} from "./interfaces"
import type { SwapRouteDecision } from "./services/swap-routing-policy"
import { SwapRoutingPolicy } from "./services/swap-routing-policy"
import type { NormalizedSwapQuote } from "./types"
import { SwapQuoteReceivedEvent, SwapQuotesPausedEvent, SwapQuotesResumedEvent } from "./events"

/**
 * Swap Quote Store
 * Query layer - doesn't own quotes, queries aggregators for their quotes
 * Manages selection state and coordinates with aggregators
 * Uses MobX for reactivity - no reducers
 */
export class SwapQuoteStore implements ISwapQuoteStore {
  selectedAggregatorId: string | null = null
  quotesPaused: boolean = false
  pauseReason: "approval" | "execution" | null = null

  constructor(
    private readonly pluginRegistry: ISwapAggregatorPluginRegistry,
    private readonly settingsStore: ISwapAggregatorSettingsStore,
    private readonly eventBus: IEventBus,
    private readonly inputStore: ISwapInputStore,
    private readonly routingPolicy: SwapRoutingPolicy = new SwapRoutingPolicy(),
  ) {
    makeObservable(this, {
      selectedAggregatorId: observable,
      quotesPaused: observable,
      pauseReason: observable,
      allDisplayQuotes: computed,
      bestQuote: computed,
      selectedQuote: computed,
      selectedQuoteId: computed,
      isLoadingQuotes: computed,
      routeDecision: computed,
      selectAggregator: action,
      clearAggregator: action,
      clearMismatchedQuotes: action,
    })

    // Subscribe to events
    this.eventBus.subscribe(SwapQuoteReceivedEvent.TYPE, this.handleQuoteReceived.bind(this))
    this.eventBus.subscribe(SwapQuotesPausedEvent.TYPE, this.handleQuotesPaused.bind(this))
    this.eventBus.subscribe(SwapQuotesResumedEvent.TYPE, this.handleQuotesResumed.bind(this))

    // Watch for token changes and clear mismatched quotes
    reaction(
      () => [this.inputStore.inputToken?.address.toString(), this.inputStore.outputToken?.address.toString()],
      () => {
        this.clearMismatchedQuotes()
      },
    )
  }

  /**
   * Get all display quotes from all enabled aggregators
   * Queries each aggregator for its display quote (selectedQuote or latestQuote)
   * Filters out quotes that don't match the current input/output tokens
   */
  get allDisplayQuotes(): NormalizedSwapQuote[] {
    const enabledPlugins = this.pluginRegistry.getEnabled(this.settingsStore)
    const currentInputToken = this.inputStore.inputToken
    const currentOutputToken = this.inputStore.outputToken
    const currentInputAmountRaw = this.getCurrentInputAmountRaw()

    // If no tokens are set, return empty array
    if (!currentInputToken || !currentOutputToken || currentInputAmountRaw === null) {
      return []
    }

    return enabledPlugins
      .map((plugin) => plugin.displayQuote)
      .filter((quote): quote is NormalizedSwapQuote => {
        if (!quote) return false

        // Validate quote tokens match current tokens
        // If tokens don't match, this quote is stale and should be discarded
        const inputMatches = quote.inputToken.address.equals(currentInputToken.address)
        const outputMatches = quote.outputToken.address.equals(currentOutputToken.address)
        const amountMatches = quote.inputAmountRaw === currentInputAmountRaw

        return inputMatches && outputMatches && amountMatches
      })
  }

  /**
   * Get the best quote (highest outputAmountRaw)
   */
  get bestQuote(): NormalizedSwapQuote | undefined {
    return this.routeDecision.quote
  }

  /**
   * Get the quote from the selected aggregator
   */
  get selectedQuote(): NormalizedSwapQuote | undefined {
    if (!this.selectedAggregatorId) {
      return this.routeDecision.quote
    }

    const plugin = this.pluginRegistry.get(this.selectedAggregatorId)
    if (!plugin) {
      return undefined
    }

    return plugin.displayQuote ?? undefined
  }

  get routeDecision(): SwapRouteDecision {
    return this.routingPolicy.chooseRoute(this.allDisplayQuotes)
  }

  /**
   * Get selected quote ID for UI (computed from selectedQuote)
   */
  get selectedQuoteId(): string | null {
    const quote = this.selectedQuote
    if (!quote) {
      return null
    }
    return `${quote.aggregatorId}-${quote.quoteId}`
  }

  /**
   * Check if any enabled plugin is currently loading quotes
   */
  get isLoadingQuotes(): boolean {
    const enabledPlugins = this.pluginRegistry.getEnabled(this.settingsStore)
    return enabledPlugins.some((plugin) => plugin.isLoadingQuotes)
  }

  /**
   * Select an aggregator (calls aggregator.selectCurrentQuote())
   */
  selectAggregator(id: string): void {
    const plugin = this.pluginRegistry.get(id)
    if (!plugin) {
      return
    }

    this.selectedAggregatorId = id
    plugin.selectCurrentQuote()
  }

  /**
   * Clear the selected aggregator and all quotes from all plugins
   */
  clearAggregator(): void {
    // Clear the selected aggregator
    this.selectedAggregatorId = null

    // Clear quotes from all plugins
    const allPlugins = this.pluginRegistry.getAll()
    for (const plugin of allPlugins) {
      plugin.clearQuotes()
    }
  }

  /**
   * Clear quotes from plugins that don't match the current input/output tokens
   * Called automatically when tokens change via MobX reaction
   */
  clearMismatchedQuotes(): void {
    const currentInputToken = this.inputStore.inputToken
    const currentOutputToken = this.inputStore.outputToken

    if (!currentInputToken || !currentOutputToken) {
      return
    }

    const allPlugins = this.pluginRegistry.getAll()
    for (const plugin of allPlugins) {
      const quote = plugin.displayQuote
      if (quote) {
        // Check if quote tokens match current tokens
        const inputMatches = quote.inputToken.address.equals(currentInputToken.address)
        const outputMatches = quote.outputToken.address.equals(currentOutputToken.address)

        // If tokens don't match, clear this plugin's quotes
        if (!inputMatches || !outputMatches) {
          plugin.clearQuotes()
        }
      }
    }

    // If selected aggregator's quote was cleared, clear selection
    if (this.selectedAggregatorId) {
      const selectedPlugin = this.pluginRegistry.get(this.selectedAggregatorId)
      if (selectedPlugin && !selectedPlugin.displayQuote) {
        this.selectedAggregatorId = null
      }
    }
  }

  /**
   * Handle quote received event (for UI updates)
   */
  private handleQuoteReceived(_event: SwapQuoteReceivedEvent): void {
    // QuoteStore doesn't own quotes, but we can trigger reactivity
    // by accessing computed properties
    // This is mainly for UI updates
  }

  /**
   * Handle quotes paused event
   */
  private handleQuotesPaused(event: SwapQuotesPausedEvent): void {
    runInAction(() => {
      this.quotesPaused = true
      this.pauseReason = event.reason
    })
  }

  /**
   * Handle quotes resumed event
   */
  private handleQuotesResumed(_event: SwapQuotesResumedEvent): void {
    runInAction(() => {
      this.quotesPaused = false
      this.pauseReason = null
    })
  }

  private getCurrentInputAmountRaw(): bigint | null {
    const currentInputToken = this.inputStore.inputToken
    const inputAmount = this.inputStore.inputAmount

    if (!currentInputToken || !inputAmount || Number(inputAmount) <= 0) {
      return null
    }

    try {
      return currentInputToken.parseUnits(inputAmount)
    } catch {
      return null
    }
  }
}
